// # User Management Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../auth/state';
import { db, functions } from '../../../../auth/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import UserEdit from './user_edit';
import './user_management.css';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  DialogContentText,
  Button,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Search, FilterList } from '@mui/icons-material';
import { MdVerified, MdWarning } from 'react-icons/md';
import { FaIdCard } from 'react-icons/fa'; // # Import subscription icon

const UserManagement = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [updateError, setUpdateError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'new'
  const [sortConfig, setSortConfig] = useState({ field: 'metadata.createdAt', direction: 'desc' });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // # Filter states
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    profileComplete: '',
    verified: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // # Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        setError(null);

        const usersRef = collection(db, 'users');
        const usersQuery = query(usersRef);
        const querySnapshot = await getDocs(usersQuery);
        
        const usersData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            uid: doc.id,
            firstName: data.firstName || '',
            middleName: data.middleName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role: data.role || 'user',
            status: data.status || 'active',
            metadata: {
              createdAt: data.metadata?.createdAt || data.profileCompleted || new Date().toISOString(),
              updatedAt: data.metadata?.updatedAt || data.updatedAt || new Date().toISOString(),
              lastSignInTime: data.metadata?.lastSignInTime || new Date().toISOString()
            }
          };
        });

        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  // # Format name
  const formatName = (user) => {
    if (!user) return 'N/A';
    
    const parts = [
      user.firstName,
      user.middleName,
      user.lastName
    ].filter(Boolean);
    
    if (parts.length > 0) {
      return parts.join(' ');
    }
    
    return user.email || 'N/A';
  };

  // # Check if profile is complete
  const isProfileComplete = (user) => {
    if (!user) return false;
    
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'gender',
      'nationality',
      'university',
      'phase',
      'gpa'
    ];
    
    return requiredFields.every(field => user[field]);
  };

  // # Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // # Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setEditData({
      firstName: user.firstName || '',
      middleName: user.middleName || '',
      lastName: user.lastName || '',
      phoneNumber: user.phoneNumber || '',
      gender: user.gender || '',
      nationality: user.nationality || '',
      university: user.university || '',
      phase: user.phase || '',
      gpa: user.gpa || '',
      status: user.status || 'active',
      role: user.role || 'user',
      selectedCountry: user.selectedCountry || { code: '+20', name_en: 'Egypt', flag: '🇪🇬' }
    });
  };

  // # Handle save
  const handleSave = async () => {
    try {
      setUpdateError(null);
      const editUserFunction = httpsCallable(functions, 'editUser');
      
      const result = await editUserFunction({
        uid: selectedUser.uid,
        updates: editData
      });

      if (result.data.success) {
        const updatedUser = {
          ...selectedUser,
          ...result.data.updates
        };
        
        setUsers(prevUsers => prevUsers.map(user => 
          user.uid === selectedUser.uid 
            ? updatedUser
            : user
        ));
        setSelectedUser(updatedUser);
        setIsEditing(false);
        
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
      } else {
        throw new Error(result.data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setUpdateError(error.message || 'Failed to update user. Please try again.');
      
      setSnackbar({
        open: true,
        message: `Error updating user: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // # Handle delete
  const handleDelete = async (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // # Execute delete
  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');
      
      const result = await deleteUserFunction({
        uid: userToDelete.uid,
        email: userToDelete.email
      });

      if (result.data.success) {
        setSnackbar({
          open: true,
          message: 'User deleted successfully',
          severity: 'success'
        });
        
        setUsers(prevUsers => 
          prevUsers.filter(user => user.uid !== userToDelete.uid)
        );
        
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: `Error deleting user: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  // # Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // # Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Reset filters
  const resetFilters = () => {
    setFilters({
      role: '',
      status: '',
      profileComplete: '',
      verified: ''
    });
  };

  // # Add sorting function
  const sortUsers = (users) => {
    return [...users].sort((a, b) => {
      const aValue = a.metadata?.createdAt || '';
      const bValue = b.metadata?.createdAt || '';
      return sortConfig.direction === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  // # Add function to handle sort toggle
  const handleSort = () => {
    setSortConfig(prev => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // # Filter users based on tab and search/filters
  const getFilteredUsers = () => {
    let filtered = [...users];
    
    // Apply tab filter
    if (activeTab === 'new') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(user => {
        const createdAt = new Date(user.metadata?.createdAt);
        return createdAt >= sevenDaysAgo;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        return (
          formatName(user).toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.role?.toLowerCase().includes(searchLower) ||
          user.status?.toLowerCase().includes(searchLower) ||
          (user.phoneNumber && user.phoneNumber.includes(searchLower)) ||
          (user.selectedCountry?.code && `${user.selectedCountry.code} ${user.phoneNumber || ''}`.toLowerCase().includes(searchLower)) ||
          (user.username && user.username.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply other filters
    if (filters.role) filtered = filtered.filter(user => user.role === filters.role);
    if (filters.status) filtered = filtered.filter(user => user.status === filters.status);
    if (filters.profileComplete) {
      filtered = filtered.filter(user => 
        filters.profileComplete === 'complete' ? isProfileComplete(user) : !isProfileComplete(user)
      );
    }
    if (filters.verified) {
      filtered = filtered.filter(user => 
        filters.verified === 'verified' ? user.emailVerified : !user.emailVerified
      );
    }

    // Apply sorting
    return sortUsers(filtered);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="usr_mng_admin-page">
      <SharedHeader />
      
      <main className="usr_mng_admin-content">
        {/* # Page Header */}
        <div className="usr_mng_admin-header">
          <div className="usr_mng_header-left">
            <button 
              className="usr_mng_back-button"
              onClick={() => navigate('/admin')}
            >
              <i className="fas fa-arrow-left"></i> Back to Dashboard
            </button>
            <h1>User Management</h1>
          </div>

          {/* # Users Stats Cards */}
          <div className="usr_mng_stats-cards">
            <div className="usr_mng_stat-card">
              <div className="usr_mng_stat-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="usr_mng_stat-content">
                <span className="usr_mng_stat-label">Total</span>
                <span className="usr_mng_stat-value">{users.length}</span>
              </div>
            </div>

            <div className="usr_mng_stat-card">
              <div className="usr_mng_stat-icon active">
                <i className="fas fa-user-check"></i>
              </div>
              <div className="usr_mng_stat-content">
                <span className="usr_mng_stat-label">Active</span>
                <span className="usr_mng_stat-value">
                  {users.filter(user => user.status === 'active').length}
                </span>
              </div>
            </div>

            <div className="usr_mng_stat-card">
              <div className="usr_mng_stat-icon suspended">
                <i className="fas fa-user-slash"></i>
              </div>
              <div className="usr_mng_stat-content">
                <span className="usr_mng_stat-label">Suspended</span>
                <span className="usr_mng_stat-value">
                  {users.filter(user => user.status === 'suspended').length}
                </span>
              </div>
            </div>

            <div className="usr_mng_stat-card">
              <div className="usr_mng_stat-icon verified">
                <i className="fas fa-user-shield"></i>
              </div>
              <div className="usr_mng_stat-content">
                <span className="usr_mng_stat-label">Verified</span>
                <span className="usr_mng_stat-value">
                  {users.filter(user => user.emailVerified).length}
                </span>
              </div>
            </div>

            <div className="usr_mng_stat-card">
              <div className="usr_mng_stat-icon complete">
                <i className="fas fa-user-graduate"></i>
              </div>
              <div className="usr_mng_stat-content">
                <span className="usr_mng_stat-label">Complete</span>
                <span className="usr_mng_stat-value">
                  {users.filter(user => isProfileComplete(user)).length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* # Add Tabs */}
        <div className="usr_mng_tabs">
          <div className="usr_mng_tabs-left">
            <button 
              className={`usr_mng_tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Users
            </button>
            <button 
              className={`usr_mng_tab ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => setActiveTab('new')}
            >
              New Users (Last 7 Days)
            </button>
          </div>
          <div 
            className="usr_mng_subscription-nav"
            onClick={() => navigate('/admin/subscriptions/management')}
            title="Go to Subscription Management"
          >
            <FaIdCard />
          </div>
        </div>

        {/* # Error Message */}
        {error && (
          <div className="usr_mng_error-message">
            {error}
          </div>
        )}

        {/* # Search and Filter Section */}
        <div className="usr_mng_search-filter-container">
          <div className="usr_mng_search-row">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, phone, role, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="usr_mng_search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="outlined" 
              className="usr_mng_filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterList />}
            >
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="usr_mng_filters-container">
              <Grid container spacing={2} className="usr_mng_filter-grid">
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={filters.role}
                      onChange={handleFilterChange}
                      label="Role"
                    >
                      <MenuItem value="">All Roles</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="mentor">Mentor</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Profile</InputLabel>
                    <Select
                      name="profileComplete"
                      value={filters.profileComplete}
                      onChange={handleFilterChange}
                      label="Profile"
                    >
                      <MenuItem value="">All Profiles</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                      <MenuItem value="incomplete">Incomplete</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Verification</InputLabel>
                    <Select
                      name="verified"
                      value={filters.verified}
                      onChange={handleFilterChange}
                      label="Verification"
                    >
                      <MenuItem value="">All Users</MenuItem>
                      <MenuItem value="verified">Verified</MenuItem>
                      <MenuItem value="unverified">Unverified</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <div className="usr_mng_filter-actions">
                <Button 
                  variant="outlined" 
                  color="inherit"
                  onClick={resetFilters}
                  size="small"
                >
                  Reset Filters
                </Button>
                <div className="usr_mng_filter-stats">
                  {getFilteredUsers().length} of {users.length} users
                </div>
              </div>
            </div>
          )}
        </div>

        {/* # Users Table */}
        <div className="usr_mng_users-table-container">
          <table className="usr_mng_users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Profile</th>
                <th onClick={handleSort} className="usr_mng_sortable-header">
                  Created At {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredUsers().length > 0 ? (
                getFilteredUsers().map((user) => (
                  <tr key={user.uid} onClick={() => handleUserSelect(user)}>
                    <td>
                      <div className="usr_mng_user-info-cell">
                        <div className="usr_mng_user-avatar">
                          <img src={user.photoURL || '/icons/pfp.png'} alt={formatName(user)} />
                        </div>
                        <div className="usr_mng_user-name">
                          {formatName(user)}
                        </div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.phoneNumber ? `${user.selectedCountry?.code || ''} ${user.phoneNumber}` : 'N/A'}</td>
                    <td>
                      <span className={`usr_mng_role-badge ${user.role || 'user'}`}>
                        {user.role || 'User'}
                      </span>
                    </td>
                    <td>
                      <span className={`usr_mng_status-badge ${user.status || 'active'}`}>
                        {user.status || 'Active'}
                      </span>
                    </td>
                    <td>
                      <span className={`usr_mng_profile-badge ${isProfileComplete(user) ? 'complete' : 'incomplete'}`}>
                        {isProfileComplete(user) ? (
                          <><MdVerified className="usr_mng_profile-icon" /> Complete</>
                        ) : (
                          <><MdWarning className="usr_mng_profile-icon" /> Incomplete</>
                        )}
                      </span>
                    </td>
                    <td>{formatDate(user.metadata?.createdAt)}</td>
                    <td>{formatDate(user.metadata?.lastSignInTime)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="usr_mng_no-results">
                    {loading ? 'Loading users...' : 
                     error ? error :
                     (searchTerm || filters.role || filters.status || filters.profileComplete || filters.verified) ? 
                       'No users found matching your filters' :
                       activeTab === 'new' ? 'No new users in the last 7 days' : 'No users found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* # User Edit Dialog */}
        <UserEdit
          open={selectedUser !== null}
          onClose={() => setSelectedUser(null)}
          selectedUser={selectedUser}
          editData={editData}
          setEditData={setEditData}
          onSave={handleSave}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          onDelete={handleDelete}
          isDeleting={isDeleting}
          updateError={updateError}
        />

        {/* # Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>
            Confirm User Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {formatName(userToDelete)}?
              <br /><br />
              <strong>This will permanently delete:</strong>
              <ul>
                <li>User account and authentication</li>
                <li>All user data and documents</li>
                <li>Subscriptions and access</li>
                <li>Notes and bookmarks</li>
                <li>Feedback and reviews</li>
              </ul>
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              color="inherit"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* # Snackbar */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default UserManagement;
